"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Board = void 0;
const position_1 = require("./position");
class Board {
    constructor(width, height) {
        this.positionToPieceId = new Map();
        this.pieceIdToPosition = new Map();
        this.width = width;
        this.height = height;
    }
    get pieceCount() {
        return this.pieceIdToPosition.size;
    }
    clear() {
        this.positionToPieceId.clear();
        this.pieceIdToPosition.clear();
    }
    clone() {
        const newBoard = new Board(this.width, this.height);
        for (const [pieceId, packed] of this.pieceIdToPosition.entries()) {
            newBoard.setPiece(pieceId, (0, position_1.unpackX)(packed), (0, position_1.unpackY)(packed));
        }
        return newBoard;
    }
    containsPiece(pieceId) {
        return this.pieceIdToPosition.has(pieceId);
    }
    getPiecePosition(pieceId) {
        const packed = this.pieceIdToPosition.get(pieceId);
        return packed === undefined ? null : [(0, position_1.unpackX)(packed), (0, position_1.unpackY)(packed)];
    }
    getPieceIdAtPosition(x, y) {
        if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
            throw new Error(`Position out of bounds: (${x}, ${y}) on board size (${this.width}, ${this.height})`);
        }
        return this.positionToPieceId.get((0, position_1.packPosition)(x, y)) || null;
    }
    getAllPieces() {
        const pieces = [];
        for (const [pieceId, packed] of this.pieceIdToPosition.entries()) {
            pieces.push({
                id: pieceId,
                x: (0, position_1.unpackX)(packed),
                y: (0, position_1.unpackY)(packed),
            });
        }
        return pieces;
    }
    forEachPiece(callback) {
        for (const [pieceId, packed] of this.pieceIdToPosition.entries()) {
            callback(pieceId, (0, position_1.unpackX)(packed), (0, position_1.unpackY)(packed));
        }
    }
    mapPieces(callback) {
        const results = [];
        for (const [pieceId, packed] of this.pieceIdToPosition.entries()) {
            results.push(callback(pieceId, (0, position_1.unpackX)(packed), (0, position_1.unpackY)(packed)));
        }
        return results;
    }
    reducePieces(callback, initialValue) {
        let accumulator = initialValue;
        for (const [pieceId, packed] of this.pieceIdToPosition.entries()) {
            accumulator = callback(accumulator, pieceId, (0, position_1.unpackX)(packed), (0, position_1.unpackY)(packed));
        }
        return accumulator;
    }
    setPieces(pieces) {
        this.positionToPieceId.clear();
        this.pieceIdToPosition.clear();
        for (const piece of pieces) {
            if (piece.x < 0 ||
                piece.x >= this.width ||
                piece.y < 0 ||
                piece.y >= this.height) {
                throw new Error(`Position out of bounds: (${piece.x}, ${piece.y}) on board size (${this.width}, ${this.height})`);
            }
            const key = (0, position_1.packPosition)(piece.x, piece.y);
            this.positionToPieceId.set(key, piece.id);
            this.pieceIdToPosition.set(piece.id, key);
        }
    }
    setPiece(pieceId, x, y) {
        if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
            throw new Error(`Position out of bounds: (${x}, ${y}) on board size (${this.width}, ${this.height})`);
        }
        const key = (0, position_1.packPosition)(x, y);
        if (this.positionToPieceId.has(key)) {
            throw new Error(`Position (${x}, ${y}) is already occupied`);
        }
        const previousKey = this.pieceIdToPosition.get(pieceId);
        if (previousKey !== undefined) {
            this.positionToPieceId.delete(previousKey);
        }
        this.positionToPieceId.set(key, pieceId);
        this.pieceIdToPosition.set(pieceId, key);
    }
    removePiece(pieceId) {
        const key = this.pieceIdToPosition.get(pieceId);
        if (key !== undefined) {
            this.pieceIdToPosition.delete(pieceId);
            this.positionToPieceId.delete(key);
        }
    }
    swapPieces(pieceIdA, pieceIdB) {
        const keyA = this.pieceIdToPosition.get(pieceIdA);
        const keyB = this.pieceIdToPosition.get(pieceIdB);
        if (keyA === undefined) {
            throw new Error(`Piece with ID ${pieceIdA} does not exist on the board`);
        }
        if (keyB === undefined) {
            throw new Error(`Piece with ID ${pieceIdB} does not exist on the board`);
        }
        this.pieceIdToPosition.set(pieceIdA, keyB);
        this.pieceIdToPosition.set(pieceIdB, keyA);
        this.positionToPieceId.set(keyA, pieceIdB);
        this.positionToPieceId.set(keyB, pieceIdA);
    }
}
exports.Board = Board;
